import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Check, Clock, TrendingUp, Calendar, Bell, BarChart3 } from 'lucide-react';

const PRESET_HABITS = [
  { name: 'Drink Water', icon: '💧', suggested: '8 glasses a day' },
  { name: 'Run', icon: '🏃', suggested: '30 minutes' },
  { name: 'Study', icon: '📚', suggested: '1 hour' },
  { name: 'Eat Healthy', icon: '🥗', suggested: '3 balanced meals' },
];

export default function HabitTracker() {
  const [habits, setHabits] = useState([]);
  const [activeSection, setActiveSection] = useState('today'); // today or progress
  const [progressView, setProgressView] = useState('monthly'); // monthly or yearly
  const [showAddModal, setShowAddModal] = useState(false);
  const [customHabitName, setCustomHabitName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [reminderDays, setReminderDays] = useState([]);
  const [reminderTime, setReminderTime] = useState('09:00');

  useEffect(() => {
    const saved = localStorage.getItem('habits');
    if (saved) {
      setHabits(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('habits', JSON.stringify(habits));
  }, [habits]);

  const addHabit = (habitName, icon = '✨') => {
    const habit = {
      id: Date.now().toString(),
      name: habitName,
      icon: icon,
      completions: {},
      reminders: {
        days: reminderDays,
        time: reminderTime
      }
    };

    setHabits([...habits, habit]);
    setShowAddModal(false);
    setCustomHabitName('');
    setSelectedPreset(null);
    setReminderDays([]);
    setReminderTime('09:00');
  };

  const deleteHabit = (id) => {
    setHabits(habits.filter(h => h.id !== id));
  };

  const toggleCompletion = (habitId, date) => {
    setHabits(habits.map(habit => {
      if (habit.id === habitId) {
        return {
          ...habit,
          completions: {
            ...habit.completions,
            [date]: !habit.completions[date]
          }
        };
      }
      return habit;
    }));
  };

  const getLast7Days = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: date.toISOString().split('T')[0],
        weekday: i === 0 ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' }),
        day: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return days;
  };

  const getDaysForProgress = (numDays) => {
    const days = [];
    for (let i = numDays - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: date.toISOString().split('T')[0],
        day: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return days;
  };

  const getSuccessRate = (habit, days) => {
    const completed = days.filter(d => habit.completions[d.date]).length;
    return days.length > 0 ? Math.round((completed / days.length) * 100) : 0;
  };

  const getTotalCompletions = (habit) => {
    return Object.values(habit.completions).filter(Boolean).length;
  };

  const getStreak = (habit) => {
    let currentStreak = 0;
    const today = new Date();
    
    for (let i = 0; i < 365; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      if (habit.completions[dateStr]) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    return currentStreak;
  };

  const toggleReminderDay = (day) => {
    setReminderDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const weekDays = getLast7Days();
  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 sm:p-8 text-white">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold mb-2">Habit Tracker</h1>
                <p className="text-indigo-100">Build better habits, one day at a time</p>
              </div>
              
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-white text-indigo-600 px-6 py-3 rounded-lg hover:bg-indigo-50 transition flex items-center gap-2 font-semibold"
              >
                <Plus size={20} />
                Add Habit
              </button>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveSection('today')}
              className={`flex-1 px-6 py-4 font-semibold transition flex items-center justify-center gap-2 ${
                activeSection === 'today'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Calendar size={20} />
              Today & This Week
            </button>
            <button
              onClick={() => setActiveSection('progress')}
              className={`flex-1 px-6 py-4 font-semibold transition flex items-center justify-center gap-2 ${
                activeSection === 'progress'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <TrendingUp size={20} />
              Progress & Stats
            </button>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Section 1: Today & This Week */}
            {activeSection === 'today' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Habits This Week</h2>
                  <p className="text-gray-600">Check off your habits as you complete them each day</p>
                </div>

                {habits.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl">
                    <Calendar size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-lg mb-2">No habits yet!</p>
                    <p className="text-sm">Click "Add Habit" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {habits.map((habit) => {
                      const todayDate = new Date().toISOString().split('T')[0];
                      
                      const streak = getStreak(habit);
                      const totalCompletions = getTotalCompletions(habit);
                      
                      return (
                        <div key={habit.id} className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-4xl">{habit.icon}</span>
                              <div className="flex-1">
                                <h3 className="text-xl font-semibold text-gray-800">{habit.name}</h3>
                                {habit.reminders.days.length > 0 && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                    <Bell size={14} />
                                    <span>{habit.reminders.days.join(', ')} at {habit.reminders.time}</span>
                                  </div>
                                )}
                                {/* Compact Stats */}
                                <div className="flex items-center gap-4 mt-2">
                                  <div className={`flex items-center gap-1 text-sm font-semibold ${
                                    streak > 0 ? 'text-orange-600' : 'text-gray-400'
                                  }`}>
                                    <span className="text-lg">{streak > 0 ? '🔥' : '⚪'}</span>
                                    <span>{streak} day streak</span>
                                  </div>
                                  <div className={`flex items-center gap-1 text-sm font-semibold ${
                                    totalCompletions > 0 ? 'text-green-600' : 'text-gray-400'
                                  }`}>
                                    <span className="text-lg">{totalCompletions > 0 ? '✅' : '⚪'}</span>
                                    <span>{totalCompletions} completed</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => deleteHabit(habit.id)}
                              className="text-red-500 hover:text-red-700 transition p-2"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          <div className="grid grid-cols-7 gap-2">
                            {weekDays.map(({ date, weekday, day, month }) => {
                              const isCompleted = habit.completions[date];
                              const isToday = date === todayDate;
                              
                              return (
                                <div key={date} className="text-center">
                                  <div className={`text-xs font-medium mb-1 ${isToday ? 'text-indigo-600' : 'text-gray-600'}`}>
                                    {weekday}
                                  </div>
                                  <div className="text-xs text-gray-500 mb-2">
                                    {month} {day}
                                  </div>
                                  <button
                                    onClick={() => toggleCompletion(habit.id, date)}
                                    className={`w-full aspect-square rounded-lg transition flex items-center justify-center ${
                                      isCompleted
                                        ? 'bg-green-500 text-white shadow-lg scale-105'
                                        : isToday
                                        ? 'bg-indigo-100 border-2 border-indigo-400 hover:bg-indigo-200'
                                        : 'bg-white border-2 border-gray-300 hover:border-indigo-400'
                                    }`}
                                  >
                                    {isCompleted && <Check size={20} className="mx-auto" />}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Section 2: Progress & Stats */}
            {activeSection === 'progress' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Progress & Statistics</h2>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setProgressView('monthly')}
                      className={`px-4 py-2 rounded-lg transition font-semibold ${
                        progressView === 'monthly'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Monthly (30 days)
                    </button>
                    <button
                      onClick={() => setProgressView('yearly')}
                      className={`px-4 py-2 rounded-lg transition font-semibold ${
                        progressView === 'yearly'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Yearly (365 days)
                    </button>
                  </div>
                </div>

                {habits.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl">
                    <BarChart3 size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-lg mb-2">No habits to track yet!</p>
                    <p className="text-sm">Add habits to see your progress</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {habits.map((habit) => {
                      const days = getDaysForProgress(progressView === 'monthly' ? 30 : 365);
                      const successRate = getSuccessRate(habit, days);
                      const completedCount = days.filter(d => habit.completions[d.date]).length;
                      const totalDays = days.length;
                      const streak = getStreak(habit);
                      const totalCompletions = getTotalCompletions(habit);
                      
                      return (
                        <div key={habit.id} className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm">
                          <div className="flex items-center gap-3 mb-6">
                            <span className="text-4xl">{habit.icon}</span>
                            <div className="flex-1">
                              <h3 className="text-xl font-semibold text-gray-800">{habit.name}</h3>
                              <p className="text-sm text-gray-600">
                                {progressView === 'monthly' ? 'Last 30 days' : 'Last 365 days'}
                              </p>
                            </div>
                          </div>

                          {/* Exciting Large Stats Cards */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white shadow-lg transform hover:scale-105 transition">
                              <div className="text-sm font-medium opacity-90 mb-1">Success Rate</div>
                              <div className="text-4xl font-bold">{successRate}%</div>
                            </div>
                            <div className="bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl p-4 text-white shadow-lg transform hover:scale-105 transition">
                              <div className="text-sm font-medium opacity-90 mb-1">Completed</div>
                              <div className="text-4xl font-bold">{completedCount}</div>
                              <div className="text-xs opacity-75">of {totalDays} days</div>
                            </div>
                            <div className={`rounded-xl p-4 text-white shadow-lg transform hover:scale-105 transition ${
                              streak > 0
                                ? 'bg-gradient-to-br from-orange-400 to-red-500'
                                : 'bg-gray-400'
                            }`}>
                              <div className="text-sm font-medium opacity-90 mb-1 flex items-center gap-1">
                                <span>{streak > 0 ? '🔥' : '⚪'}</span> Streak
                              </div>
                              <div className="text-4xl font-bold">{streak}</div>
                              <div className="text-xs opacity-75">days in a row</div>
                            </div>
                            <div className={`rounded-xl p-4 text-white shadow-lg transform hover:scale-105 transition ${
                              totalCompletions > 0
                                ? 'bg-gradient-to-br from-blue-400 to-cyan-600'
                                : 'bg-gray-400'
                            }`}>
                              <div className="text-sm font-medium opacity-90 mb-1">All Time</div>
                              <div className="text-4xl font-bold">{totalCompletions}</div>
                              <div className="text-xs opacity-75">total times</div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                              <span className="text-sm text-gray-600">{completedCount}/{totalDays} days</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-6 relative overflow-hidden shadow-inner">
                              <div 
                                className="bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                                style={{ width: `${successRate}%` }}
                              >
                                {successRate > 10 && (
                                  <span className="text-white text-xs font-bold drop-shadow">{successRate}%</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Visual Calendar (only for monthly) */}
                          {progressView === 'monthly' && (
                            <div className="mt-4">
                              <div className="text-xs font-semibold text-gray-700 mb-3">Daily Activity - Last 30 Days</div>
                              <div className="grid grid-cols-10 gap-1.5">
                                {days.map(({ date, day, month }) => (
                                  <div
                                    key={date}
                                    className={`aspect-square rounded flex flex-col items-center justify-center text-xs transition hover:scale-110 cursor-pointer ${
                                      habit.completions[date]
                                        ? 'bg-green-500 text-white font-bold shadow'
                                        : 'bg-gray-200 text-gray-400'
                                    }`}
                                    title={`${month} ${day}: ${habit.completions[date] ? 'Completed ✓' : 'Not completed'}`}
                                  >
                                    <span className="text-[10px]">{month}</span>
                                    <span>{day}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Habit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Add New Habit</h2>
            
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Choose a preset habit:</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {PRESET_HABITS.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSelectedPreset(preset);
                      setCustomHabitName('');
                    }}
                    className={`p-4 rounded-lg border-2 transition ${
                      selectedPreset?.name === preset.name
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{preset.icon}</div>
                    <div className="font-semibold text-sm">{preset.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{preset.suggested}</div>
                  </button>
                ))}
              </div>

              <div className="text-center text-gray-500 my-3">or</div>

              <input
                type="text"
                value={customHabitName}
                onChange={(e) => {
                  setCustomHabitName(e.target.value);
                  setSelectedPreset(null);
                }}
                placeholder="Create custom habit..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Reminder Settings */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Clock size={16} />
                Set Reminders (Optional)
              </h3>
              
              <div className="mb-3">
                <label className="text-xs text-gray-600 mb-2 block">Select days:</label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map(day => (
                    <button
                      key={day}
                      onClick={() => toggleReminderDay(day)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        reminderDays.includes(day)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-2 block">Time:</label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCustomHabitName('');
                  setSelectedPreset(null);
                  setReminderDays([]);
                  setReminderTime('09:00');
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const habitName = selectedPreset?.name || customHabitName.trim();
                  const icon = selectedPreset?.icon || '✨';
                  if (habitName) {
                    addHabit(habitName, icon);
                  }
                }}
                disabled={!selectedPreset && !customHabitName.trim()}
                className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Habit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}